<script lang="ts" module>
  import type { Antrieb } from '../reiseart';
  import type { Phase, Reiterschluessel } from './phasen';

  /**
   * Was die Leiste von einer offenen Reise wissen muss.
   *
   * Bewusst **nicht** `Trip`: Der Planer hält den lebenden Stand (der Name
   * ändert sich beim Tippen, die Reiseart in den Einstellungen, die Zähler mit
   * jeder Rechnung), und die Leiste steht eine Ebene darüber in `App.svelte`.
   * Was dazwischen wandert, ist diese Handvoll Werte — und nicht die ganze
   * Reise samt Stationen, von denen die Leiste nur die Anzahl zeigt.
   */
  export interface Reisestand {
    id: number;
    name: string;
    beginn: string | null;
    ende: string | null;
    stationen: number;
    antrieb: Antrieb;
    /** `settings.merkmale`, normiert — entscheidet über den Kajak-Reiter. */
    merkmale: readonly string[];
    /** Die Phase, die gerade dran ist (`phasen.jetztPhase`). */
    jetzt: Phase;
    /** Was neben einem Reiter als Zahl steht — dieselbe Auskunft wie früher in der Reiterleiste. */
    zaehler: Partial<Record<Reiterschluessel, string>>;
  }
</script>

<script lang="ts">
  /**
   * Die eine Seitenleiste — auf jeder angemeldeten Fläche dieselbe.
   *
   * ## Warum es sie gibt (Vertrag Navigation A, 14.9.2026)
   *
   * Vorher hatte jede Fläche ihre eigene Navigation: Die Reiseliste eine
   * Verweiszeile unter der Liste, die Grundlagen eine Bereichsliste links, der
   * Planer elf Reiter in zwei Gruppen über der Karte. Drei Ordnungen für eine
   * Anwendung, und keine davon sagte, wo man gerade ist und was es sonst noch
   * gibt. Wer im Planer saß, sah die Grundlagen nicht; wer in den Grundlagen
   * saß, sah die Reise nicht.
   *
   * Jetzt gibt es eine Leiste, `App.svelte` rendert sie **einmal**, und die
   * Flächen stehen daneben. Sie hat drei Teile: die Anwendung oben, die offene
   * Reise in der Mitte, Hilfe und Neuerungen unten.
   *
   * ## Warum die Reiter hier stehen und nicht mehr im Planer
   *
   * Weil sie keine Reiter mehr sind, sondern Unterpunkte einer Phase. Eine
   * Reiterleiste behauptet, elf gleichrangige Flächen nebeneinander zu haben;
   * die Phasen sagen, welche davon **jetzt** dran ist. Die Zuordnung steht in
   * `phasen.ts` und ist dort ohne Browser prüfbar.
   *
   * Deshalb auch kein `role="tablist"` mehr: Reiter und Panel stünden dafür im
   * selben Dokumentzweig, und das tun sie seit diesem Umbau nicht — die Leiste
   * steht in `App.svelte`, die Fläche im Planer. Eine Rolle, die eine
   * Beziehung behauptet, die es im Baum nicht gibt, hilft keinem Vorlesegerät.
   * Was bleibt, ist `aria-current="page"` an dem Punkt, auf dem man steht.
   */
  import Zeichen from '../../gestaltung/Zeichen.svelte';
  import Marke from '../../gestaltung/Marke.svelte';
  import { SEITEN, type Grundlagenseite } from '../grundlagen/wege';
  import { ANTRIEB_WAHL } from '../reiseart';
  import { kurztag, phasenFuer, zustandswort } from './phasen';

  interface Props {
    /** Welche der drei angemeldeten Flächen offen ist. */
    bereich: 'reisen' | 'grundlagen' | 'planer';
    /** Die gewählte Grundlagen-Seite, wenn dieser Bereich offen ist. */
    grundlagenseite?: Grundlagenseite | null;
    /** Die offene Reise — ohne sie steht in der Mitte ein Satz. */
    reise?: Reisestand | null;
    /** Der offene Reiter dieser Reise. */
    ansicht?: Reiterschluessel | null;
    onReiter?: (key: Reiterschluessel) => void;
    /** Zurück in die Reiseliste (schließt die Reise). */
    onReisen: () => void;
    /** In die Grundlagen, auf diese Seite. */
    onGrundlagen: (seite: Grundlagenseite) => void;
    /** Steht in „Was ist neu" etwas Ungelesenes? */
    neuUngelesen?: boolean;
    /** „Was ist neu" öffnen — der Aufrufer merkt sich dabei den Stand. */
    onNeu: () => void;
    /** Das Handbuch öffnen. */
    onHandbuch: () => void;
    /** Auf dem Telefon: Ist die Schublade offen? */
    offen?: boolean;
    /** Die Schublade schließen — nach jedem Griff und beim Tippen daneben. */
    onSchliessen?: () => void;
    /** Hat dieses Konto das Welterbe-Album? Dann steht es als Punkt in der ersten Gruppe. */
    welterbe?: boolean;
  }

  let {
    bereich,
    grundlagenseite = null,
    reise = null,
    ansicht = null,
    onReiter,
    onReisen,
    onGrundlagen,
    neuUngelesen = false,
    onNeu,
    onHandbuch,
    offen = false,
    onSchliessen,
    welterbe = false
  }: Props = $props();

  /**
   * Die Phasen dieser Reise mit ihren sichtbaren Reitern.
   *
   * Leer, solange keine Reise offen ist — dann steht an dieser Stelle der
   * Satz, der sagt, was hier stünde. Ein leerer Block wäre eine Lücke, die
   * aussieht wie ein Ladefehler.
   */
  const phasen = $derived(
    reise ? phasenFuer({ antrieb: reise.antrieb, merkmale: reise.merkmale }) : []
  );

  /** `18.9. – 4.10. · 168 Stationen · Elektro` — die Zeile unter dem Reisenamen. */
  const reisezeile = $derived.by(() => {
    if (!reise) return '';
    const teile: string[] = [];
    const von = kurztag(reise.beginn);
    const bis = kurztag(reise.ende);
    if (von && bis) teile.push(`${von} – ${bis}`);
    else if (von) teile.push(`ab ${von}`);
    teile.push(reise.stationen === 1 ? '1 Station' : `${reise.stationen} Stationen`);
    const art = ANTRIEB_WAHL.find((w) => w.wert === reise.antrieb);
    if (art) teile.push(art.label);
    return teile.join(' · ');
  });

  /**
   * Ist dieser Zähler eine Aufgabe?
   *
   * `1 offen` an Schlafen oder Vor der Abfahrt ist etwas anderes als `17 Tage`
   * an Plan: Das eine wartet auf jemanden, das andere ist eine Auskunft. Die
   * Unterscheidung am Wort und nicht an einer zweiten Liste — der Zähler wird
   * an einer Stelle formuliert (`Planner.zaehler`), und wer dort „offen"
   * schreibt, meint genau das.
   */
  function istAufgabe(text: string | undefined): boolean {
    return !!text && /offen|neu/.test(text);
  }

  /** Ein Griff in der Leiste schließt auf dem Telefon die Schublade. */
  function griff(tun: () => void) {
    tun();
    onSchliessen?.();
  }
</script>

<!-- Der Vorhang hinter der Schublade. Nur auf dem Telefon sichtbar (die CSS-Regel
     steht unten); auf dem Schreibtisch ist die Leiste kein Dialog, sondern eine
     Spalte, und ein Vorhang wäre dort eine Behauptung, man müsse sie wegklicken. -->
{#if offen}
  <button
    type="button"
    class="vorhang"
    aria-label="Menü schließen"
    onclick={() => onSchliessen?.()}
  ></button>
{/if}

<nav class="leiste" class:offen aria-label="Bereiche und Reise">
  <div class="marke">
    <Zeichen name="stationen" groesse={22} />
    <span>Roadtrip</span>
  </div>

  <!-- ── 1 · Die Anwendung ────────────────────────────────────────── -->
  <ul class="gruppe">
    <li>
      <button
        type="button"
        class="bereich"
        class:aktiv={bereich === 'reisen'}
        aria-current={bereich === 'reisen' ? 'page' : undefined}
        onclick={() => griff(onReisen)}
      >
        <Zeichen name="stationen" groesse={16} />
        <span class="wort">Reisen</span>
      </button>
    </li>
    <li>
      <button
        type="button"
        class="bereich"
        class:aktiv={bereich === 'grundlagen'}
        aria-current={bereich === 'grundlagen' ? 'page' : undefined}
        onclick={() => griff(() => onGrundlagen(SEITEN[0].id))}
      >
        <Zeichen name="technik" groesse={16} />
        <span class="wort">Grundlagen</span>
      </button>

      <!--
        Die fünf Seiten der Grundlagen als Unterpunkte — aber nur, solange man
        dort steht.

        Aufgeklappt stehen sie **statt** der eigenen Bereichsliste, die bis
        heute links in `Grundlagen.svelte` stand: Zwei Listen nebeneinander,
        die dasselbe anbieten, wären zwei Meinungen darüber, wo man ist.
        Zugeklappt ist die Leiste so lang wie die Reise es verlangt — fünf
        Unterpunkte an einer Fläche, die man selten öffnet, drängten die
        Phasen nach unten.

        Die Seite „Konto und Zugang" ist die fünfte davon. Sie steht deshalb
        hier **und** eine Zeile tiefer als eigener Punkt, aber nie zugleich:
        Der eigene Punkt ist der schnelle Weg von überall her, die Unterzeile
        ist der Ort innerhalb der Grundlagen. Zweimal dasselbe Wort in einer
        Liste sähe aus wie ein Fehler.
      -->
      {#if bereich === 'grundlagen'}
        <ul class="unterpunkte">
          {#each SEITEN as s (s.id)}
            <li>
              <button
                type="button"
                class="punkt"
                class:aktiv={s.id === grundlagenseite}
                aria-current={s.id === grundlagenseite ? 'page' : undefined}
                onclick={() => griff(() => onGrundlagen(s.id))}
              >
                <span class="wort">{s.titel}</span>
                <!-- Die Marke „bald" an einer angekündigten Seite. Heute trägt
                     keine den Schalter — er beschreibt einen Zustand, den es
                     wieder geben wird, und die alte Bereichsliste zeigte sie.
                     Ohne diese drei Zeilen verschwände die Ankündigung still
                     mit dem Umzug in die Leiste. -->
                {#if s.spaeter}<Marke>bald</Marke>{/if}
              </button>
            </li>
          {/each}
        </ul>
      {/if}
    </li>
    {#if bereich !== 'grundlagen'}
      <li>
        <button type="button" class="bereich" onclick={() => griff(() => onGrundlagen('konto'))}>
          <Zeichen name="konto" groesse={16} />
          <span class="wort">Konto</span>
        </button>
      </li>
    {/if}
    <!-- Das Welterbe-Album (21.10.2026) wohnt unter `/welterbe/` in eigener
         Anwendung, wie die Verwaltung — deshalb ein Verweis und kein
         Bereich dieser Weiche. Nur, wenn das Konto den Schalter hat: Ein
         Punkt, der zuverlässig auf eine Sperrfläche führt, ist schlimmer
         als keiner. -->
    {#if welterbe}
      <li>
        <a class="bereich" href="/welterbe/">
          <Zeichen name="entdecken" groesse={16} />
          <span class="wort">Welterbe</span>
        </a>
      </li>
    {/if}
  </ul>

  <div class="strich"></div>

  <!-- ── 2 · Die offene Reise ─────────────────────────────────────── -->
  {#if reise}
    <div class="reisekopf">
      <button type="button" class="zurueck" onclick={() => griff(onReisen)}>
        <Zeichen name="zurueck" groesse={14} />
        <span>Meine Reisen</span>
      </button>
      <!-- Zweizeilig erlaubt und nicht gekürzt: Der Name ist die Auskunft
           darüber, worin man gerade steckt, und „Städte-Tour West: Turin, …"
           beantwortet die Frage nicht. -->
      <p class="reisename">{reise.name || 'Unbenannte Reise'}</p>
      <p class="reisedaten">{reisezeile}</p>
    </div>

    <div class="phasen">
      {#each phasen as p (p.id)}
        <div class="phase">
          <div class="phasenkopf">
            <span class="etikett" class:dran={p.id === reise.jetzt}>{p.titel}</span>
            <span class="zustand" class:dran={p.id === reise.jetzt}>
              {zustandswort(p.id, reise.jetzt, reise.beginn)}
            </span>
          </div>
          <ul>
            {#each p.reiter as r (r.key)}
              {@const zahl = reise.zaehler[r.key]}
              <li>
                <button
                  type="button"
                  class="punkt"
                  class:aktiv={ansicht === r.key}
                  aria-current={ansicht === r.key ? 'page' : undefined}
                  onclick={() => griff(() => onReiter?.(r.key))}
                >
                  <span class="wort">{r.label}</span>
                  {#if zahl}
                    <span class="zahl" class:aufgabe={istAufgabe(zahl)}>{zahl}</span>
                  {/if}
                </button>
              </li>
            {/each}
          </ul>
        </div>
      {/each}
    </div>
  {:else}
    <p class="leer">
      Öffne eine Reise, dann stehen hier ihre Phasen: Entwerfen, Rechnen,
      Vorbereiten, Unterwegs, Erinnern.
    </p>
  {/if}

  <div class="fueller"></div>
  <div class="strich"></div>

  <!-- ── 3 · Unten: Neuerungen und Handbuch ───────────────────────── -->
  <ul class="gruppe">
    <li>
      <button type="button" class="bereich" onclick={() => griff(onNeu)}>
        <Zeichen name="funke" groesse={16} />
        <span class="wort">Was ist neu</span>
        {#if neuUngelesen}
          <!-- Der Punkt trägt seine Auskunft als Text mit: Eine Farbe allein
               sagt einem Vorlesegerät nichts, und rot-auf-weiß ist genau die
               Sorte Zeichen, die im Dunkelmodus und bei Farbenblindheit
               verschwindet. -->
          <span class="punktmarke" role="status">
            <span class="nurlesbar">ungelesen</span>
          </span>
        {/if}
      </button>
    </li>
    <li>
      <button type="button" class="bereich" onclick={() => griff(onHandbuch)}>
        <Zeichen name="frage" groesse={16} />
        <span class="wort">Handbuch</span>
      </button>
    </li>
  </ul>
</nav>

<style>
  /* Papierweiß auf dem Sandton der Anwendung: Die Leiste liegt vorn, sie ist
     nicht angemalt. Dieselbe Entscheidung wie beim aktiven Reiter der alten
     Leiste — Grün bleibt die Farbe der Route. */
  .leiste {
    width: 260px;
    background: var(--surface);
    border-right: 1px solid var(--border);
    padding: var(--raum-6) var(--raum-5);
    display: flex;
    flex-direction: column;
    gap: var(--raum-5);
    overflow-y: auto;
    min-height: 0;
  }

  .marke {
    display: flex;
    align-items: center;
    gap: var(--raum-5);
    padding: var(--raum-1) var(--raum-4) var(--raum-3);
    font-weight: var(--gw-halb);
    color: var(--accent);
  }
  .marke span {
    color: var(--text);
  }

  .gruppe,
  .unterpunkte,
  .phase ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--raum-1);
  }

  /* Ein Bereich und ein Punkt sind dieselbe Sorte Zeile — nur trägt der
     Bereich ein Zeichen und der Punkt steht eingerückt darunter. Deshalb eine
     gemeinsame Grundregel und zwei kurze Ergänzungen. */
  .bereich,
  .punkt {
    width: 100%;
    display: flex;
    align-items: center;
    gap: var(--raum-5);
    border: 0;
    background: none;
    border-radius: var(--radius-klein);
    /* Eng in der Höhe, luftig in der Breite: Elf Punkte, fünf Phasenköpfe und
       fünf Bereiche müssen zusammen in ein 900 px hohes Fenster passen, sonst
       steht das Handbuch unter dem Falz. */
    padding: var(--raum-2) var(--raum-5);
    font: inherit;
    font-size: var(--fs-panel);
    color: var(--text-dim);
    text-align: left;
    /* 28 px auf dem Schreibtisch, 44 auf dem Telefon: `--tippziel-neben` fällt
       auf Berührgeräten von selbst auf das volle Maß zurück (siehe
       `token/raum.css`). Eine Leiste mit elf 44-px-Zeilen wäre auf einem
       Laptop höher als das Fenster. */
    min-height: var(--tippziel-neben);
    transition: color 140ms ease-out, background-color 140ms ease-out;
  }
  /* Der Verweis aufs Welterbe-Album ist ein `<a>` in der Kleidung der Knöpfe. */
  a.bereich {
    text-decoration: none;
    box-sizing: border-box;
  }
  .bereich:hover:not(.aktiv),
  .punkt:hover:not(.aktiv) {
    color: var(--text);
    background: var(--surface-2);
  }
  .bereich.aktiv,
  .punkt.aktiv {
    background: var(--accent-weich);
    color: var(--text);
    font-weight: var(--gw-halb);
  }

  .wort {
    flex: 1;
    min-width: 0;
  }

  /* Die Unterpunkte rücken ein — und zwar so weit, dass sie unter dem Wort
     ihres Bereichs beginnen und nicht unter dessen Zeichen. */
  .unterpunkte {
    padding-left: var(--raum-9);
  }

  .strich {
    border-top: 1px solid var(--border);
  }

  /* Drückt Handbuch und Neuerungen nach unten, solange Platz da ist. Läuft die
     Leiste über, scrollt sie und der Füller ist null hoch — genau richtig:
     Dann stehen sie hinter den Phasen, wo man sie sucht. */
  .fueller {
    flex: 1;
    min-height: 0;
  }

  .reisekopf {
    display: flex;
    flex-direction: column;
    gap: var(--raum-1);
    padding: 0 var(--raum-2);
  }

  .zurueck {
    display: inline-flex;
    align-items: center;
    gap: var(--raum-3);
    align-self: start;
    border: 0;
    background: none;
    padding: var(--raum-2) var(--raum-3);
    font: inherit;
    font-size: var(--fs-fein);
    color: var(--text-schwach);
    min-height: var(--tippziel-neben);
  }
  .zurueck:hover {
    color: var(--text);
  }

  .reisename {
    margin: 0;
    padding: 0 var(--raum-3);
    font-size: var(--fs-panel);
    font-weight: var(--gw-halb);
    line-height: var(--zh-eng);
    text-wrap: pretty;
  }

  .reisedaten {
    margin: 0;
    padding: 0 var(--raum-3);
    font-size: var(--fs-fein);
    color: var(--text-schwach);
    font-variant-numeric: var(--zahl-variante);
  }

  .phasen {
    display: flex;
    flex-direction: column;
    gap: var(--raum-5);
  }

  .phasenkopf {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--raum-3);
    padding: var(--raum-1) var(--raum-5);
  }

  /* Versalien in Mikroschrift: Der Phasenname ordnet, er ist kein Ziel. Wer
     ihn anklickbar machte, müsste beantworten, was dann passiert — und die
     Antwort wäre „der erste Unterpunkt", also derselbe Klick eine Zeile
     tiefer. */
  .etikett {
    font-size: var(--fs-mikro);
    text-transform: uppercase;
    letter-spacing: 0.02em;
    font-weight: var(--gw-halb);
    color: var(--text-schwach);
  }
  .zustand {
    font-size: var(--fs-mikro);
    color: var(--text-schwach);
    white-space: nowrap;
  }
  /* Die laufende Phase ist die einzige Farbe in diesem Block. Zwei hervorgehobene
     Phasen wären zwei Antworten auf die Frage, was jetzt dran ist. */
  .etikett.dran,
  .zustand.dran {
    color: var(--accent-stark);
  }
  .zustand.dran {
    font-weight: var(--gw-halb);
  }

  .zahl {
    flex: none;
    font-size: var(--fs-mikro);
    font-weight: var(--gw-normal);
    color: var(--text-schwach);
    font-variant-numeric: var(--zahl-variante);
  }
  /* Was auf jemanden wartet, steht in der Warnfarbe — was nur zählt, nicht.
     Eine Blase wäre die Grammatik von Ungelesenem; das hier sind Nächte,
     Belege und Rückmeldungen. */
  .zahl.aufgabe {
    color: var(--danger);
  }

  .leer {
    margin: 0;
    padding: var(--raum-2) var(--raum-5);
    font-size: var(--fs-fein);
    color: var(--text-schwach);
    text-wrap: pretty;
  }

  .punktmarke {
    flex: none;
    width: 8px;
    height: 8px;
    border-radius: 999px;
    background: var(--danger);
  }

  /* Für Vorlesegeräte sichtbar, für Augen nicht — dieselbe Technik wie an den
     anderen Nur-Text-Auskünften des Hauses. */
  .nurlesbar {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  /* ── Das Telefon: aus der Spalte wird eine Schublade ───────────────
     Unter 820 px ist kein Platz für 260 px Navigation neben einer Karte. Die
     Leiste fährt deshalb über die Fläche, mit einem Vorhang dahinter, und
     schließt sich nach jedem Griff (siehe `griff`). Dieselbe Grenze wie im
     Planer — eine zweite Zahl wäre eine zweite Meinung darüber, wann ein
     Fenster schmal ist. */
  .vorhang {
    display: none;
  }

  @media (max-width: 820px) {
    .vorhang {
      display: block;
      position: fixed;
      inset: 0;
      border: 0;
      padding: 0;
      background: var(--vorhang);
      z-index: 40;
    }

    .leiste {
      position: fixed;
      inset: 0 auto 0 0;
      z-index: 41;
      width: min(300px, 86vw);
      box-shadow: 6px 0 24px rgb(0 0 0 / 18%);
      transform: translateX(-100%);
      transition: transform 160ms ease-out;
    }
    .leiste.offen {
      transform: translateX(0);
    }
    /* Eine geschlossene Schublade ist kein Ziel für die Tabulatortaste. Ohne
       das wanderte der Fokus in eine Liste, die niemand sieht. */
    .leiste:not(.offen) {
      visibility: hidden;
    }

    /* Volle Tippziele, sobald die Leiste eine Schublade ist.
       `--tippziel-neben` wächst von selbst auf 44 px, aber erst bei
       `(pointer: coarse)` — und ein auf 390 px gezogenes Schreibtischfenster
       ist das nicht. Genau daran wird diese Fläche abgenommen; ein Maß, das
       nur auf echtem Glas stimmt, ist im Zweifel keines. */
    .bereich,
    .punkt,
    .zurueck {
      min-height: var(--tippziel);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .leiste {
      transition: none;
    }
  }
</style>
